import { StructDeserializeStream, TransformStream } from "@yume-chan/adb";
import Struct, { placeholder } from "@yume-chan/struct";
import { ScrcpyClientForwardConnection, ScrcpyClientReverseConnection } from "../../connection.js";
import { AndroidKeyEventAction, ScrcpyControlMessageType } from "../../message.js";
import { toScrcpyOptionValue } from "../common.js";
import { parse_sequence_parameter_set } from "./sps.js";
export var ScrcpyLogLevel;
(function (ScrcpyLogLevel) {
    ScrcpyLogLevel["Verbose"] = "verbose";
    ScrcpyLogLevel["Debug"] = "debug";
    ScrcpyLogLevel["Info"] = "info";
    ScrcpyLogLevel["Warn"] = "warn";
    ScrcpyLogLevel["Error"] = "error";
})(ScrcpyLogLevel || (ScrcpyLogLevel = {}));
export var ScrcpyScreenOrientation;
(function (ScrcpyScreenOrientation) {
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["Initial"] = -2] = "Initial";
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["Unlocked"] = -1] = "Unlocked";
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["Portrait"] = 0] = "Portrait";
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["Landscape"] = 1] = "Landscape";
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["PortraitFlipped"] = 2] = "PortraitFlipped";
    ScrcpyScreenOrientation[ScrcpyScreenOrientation["LandscapeFlipped"] = 3] = "LandscapeFlipped";
})(ScrcpyScreenOrientation || (ScrcpyScreenOrientation = {}));
export class CodecOptions {
    constructor(value) {
        this.value = value;
    }
    toOptionValue() {
        const entries = Object.entries(this.value)
            .filter(([key, value]) => value !== undefined);
        if (entries.length === 0) {
            return undefined;
        }
        return entries
            .map(([key, value]) => `${key}=${value}`)
            .join(',');
    }
}
export const VideoPacket = new Struct()
    .uint64('pts')
    .uint32('size')
    .uint8Array('data', { lengthField: 'size' });
export const NO_PTS = BigInt(1) << BigInt(63);
export const ScrcpyBackOrScreenOnEvent1_16 = new Struct()
    .uint8('type', placeholder());
export const ScrcpyInjectScrollControlMessage1_16 = new Struct()
    .uint8('type', ScrcpyControlMessageType.InjectScroll)
    .uint32('pointerX')
    .uint32('pointerY')
    .uint16('screenWidth')
    .uint16('screenHeight')
    .int32('scrollX')
    .int32('scrollY');
export class ScrcpyOptions1_16 {
    constructor(value) {
        if (new.target === ScrcpyOptions1_16 &&
            value.logLevel === ScrcpyLogLevel.Verbose) {
            value.logLevel = ScrcpyLogLevel.Debug;
        }
        if (new.target === ScrcpyOptions1_16 &&
            value.lockVideoOrientation === ScrcpyScreenOrientation.Initial) {
            value.lockVideoOrientation = ScrcpyScreenOrientation.Unlocked;
        }
        this.value = value;
    }
    getArgumentOrder() {
        return [
            'logLevel',
            'maxSize',
            'bitRate',
            'maxFps',
            'lockVideoOrientation',
            'tunnelForward',
            'crop',
            'sendFrameMeta',
            'control',
            'displayId',
            'showTouches',
            'stayAwake',
            'codecOptions',
            'encoderName',
        ];
    }
    getDefaultValue() {
        return {
            logLevel: ScrcpyLogLevel.Debug,
            maxSize: 0,
            bitRate: 8000000,
            maxFps: 0,
            lockVideoOrientation: ScrcpyScreenOrientation.Unlocked,
            tunnelForward: false,
            crop: '-',
            sendFrameMeta: true,
            control: true,
            displayId: 0,
            showTouches: false,
            stayAwake: false,
            codecOptions: new CodecOptions({}),
            encoderName: '-',
        };
    }
    formatServerArguments() {
        const defaults = this.getDefaultValue();
        return this.getArgumentOrder()
            .map(key => toScrcpyOptionValue(this.value[key] || defaults[key], '-'));
    }
    createConnection(adb) {
        const options = {
            // Old scrcpy connection always have control stream no matter what the option is
            control: true,
            sendDummyByte: true,
            sendDeviceMeta: true,
        };
        if (this.value.tunnelForward) {
            return new ScrcpyClientForwardConnection(adb, options);
        }
        else {
            return new ScrcpyClientReverseConnection(adb, options);
        }
    }
    getOutputEncoderNameRegex() {
        return /\s+scrcpy --encoder-name '(.*?)'/;
    }
    createVideoStreamTransformer() {
        // Optimized path for video frames only
        if (this.value.sendFrameMeta === false) {
            return new TransformStream({
                transform(chunk, controller) {
                    controller.enqueue({
                        type: 'frame',
                        data: chunk,
                    });
                },
            });
        }
        let header;
        let deserializeStream = new StructDeserializeStream(VideoPacket);
        return {
            writable: deserializeStream.writable,
            readable: deserializeStream.readable.pipeThrough(new TransformStream({
                transform(packet, controller) {
                    if (packet.pts === NO_PTS) {
                        const sequenceParameterSet = parse_sequence_parameter_set(packet.data.slice().buffer);
                        const { profile_idc: profileIndex, constraint_set: constraintSet, level_idc: levelIndex, pic_width_in_mbs_minus1, pic_height_in_map_units_minus1, frame_mbs_only_flag, frame_crop_left_offset, frame_crop_right_offset, frame_crop_top_offset, frame_crop_bottom_offset, } = sequenceParameterSet;
                        const encodedWidth = (pic_width_in_mbs_minus1 + 1) * 16;
                        const encodedHeight = (pic_height_in_map_units_minus1 + 1) * (2 - frame_mbs_only_flag) * 16;
                        const cropLeft = frame_crop_left_offset * 2;
                        const cropRight = frame_crop_right_offset * 2;
                        const cropTop = frame_crop_top_offset * 2;
                        const cropBottom = frame_crop_bottom_offset * 2;
                        const croppedWidth = encodedWidth - cropLeft - cropRight;
                        const croppedHeight = encodedHeight - cropTop - cropBottom;
                        header = packet.data;
                        controller.enqueue({
                            type: 'configuration',
                            data: {
                                profileIndex,
                                constraintSet,
                                levelIndex,
                                encodedWidth,
                                encodedHeight,
                                cropLeft,
                                cropRight,
                                cropTop,
                                cropBottom,
                                croppedWidth,
                                croppedHeight,
                            }
                        });
                        return;
                    }
                    let frameData;
                    if (header) {
                        frameData = new Uint8Array(header.byteLength + packet.data.byteLength);
                        frameData.set(header);
                        frameData.set(packet.data, header.byteLength);
                        header = undefined;
                    }
                    else {
                        frameData = packet.data;
                    }
                    controller.enqueue({
                        type: 'frame',
                        pts: packet.pts,
                        data: frameData,
                    });
                }
            }))
        };
    }
    serializeBackOrScreenOnControlMessage(message) {
        if (message.action === AndroidKeyEventAction.Down) {
            return ScrcpyBackOrScreenOnEvent1_16.serialize(message);
        }
        return undefined;
    }
    serializeInjectScrollControlMessage(message) {
        return ScrcpyInjectScrollControlMessage1_16.serialize(message);
    }
}
//# sourceMappingURL=index.js.map